import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError, getWriter } from "../api";
import BackLink from "../components/BackLink";
import CharacterFace from "../components/CharacterFace";
import { BookList, ErrorState } from "../components/BookList";
import Placeholder from "../components/Placeholder";
import { useT } from "../i18n";

/**
 * WriterPage is a writer and every book by them this reader may read, newest
 * first (requirement 0118). Reached from a book's author link; there is no
 * list of writers. The server lists only readable books and answers 404 for a
 * writer with none, so the page never learns that a private book exists.
 */
export default function WriterPage() {
  const { writerID = "" } = useParams();
  const t = useT();
  const page = useQuery({
    queryKey: ["writer", writerID],
    queryFn: () => getWriter(writerID),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const writer = page.data?.writer;
  const missing = page.error instanceof ApiError && page.error.status === 404;

  return (
    <main className="mx-auto max-w-5xl px-4 pt-4 pb-12">
      <BackLink to="/browse">{t.browse.title}</BackLink>
      {missing ? (
        <p data-testid="writer-not-found" className="mt-6 text-sm text-muted">
          {t.writer.notFound}
        </p>
      ) : page.isError ? (
        <div className="mt-6">
          <ErrorState
            message={(page.error as Error).message || t.common.somethingWrong}
            onRetry={() => void page.refetch()}
          />
        </div>
      ) : (
        <>
          <header className="mt-4 mb-5 flex items-start gap-4">
            {writer ? (
              <CharacterFace character={writer} size={88} />
            ) : (
              <Placeholder className="h-[88px] w-[88px] shrink-0 rounded-full" />
            )}
            <div className="min-w-0">
              {writer ? (
                <h1
                  dir="auto"
                  data-testid="writer-name"
                  className="text-[1.65rem] leading-tight font-semibold tracking-tight"
                >
                  {writer.name}
                </h1>
              ) : (
                <Placeholder className="h-7 w-2/5 rounded-full" />
              )}
              {writer?.dates && (
                <p dir="auto" className="mt-1 text-sm text-ink-soft">
                  {writer.dates}
                </p>
              )}
              {writer?.bio && (
                <p dir="auto" data-testid="writer-bio" className="mt-3 max-w-prose text-sm">
                  {writer.bio}
                </p>
              )}
            </div>
          </header>
          <h2 className="mb-3 text-sm font-semibold text-muted">{t.writer.books}</h2>
          <BookList
            books={page.data?.books ?? []}
            isPending={page.isPending}
            onRetry={() => void page.refetch()}
            empty={null}
            savable
            testID="writer-books"
          />
        </>
      )}
    </main>
  );
}
